import { createFileRoute } from '@tanstack/react-router';
import { useObservabilitySocket, type ModelTokenStats, type TenantActivity, type RecentError } from '@/hooks/useObservabilitySocket';
import {
  Activity, MessageSquare, Bot, Users, AlertTriangle,
  Zap, Clock, TrendingUp, WifiOff, Loader2,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/observability')({
  component: PartnerObservabilityPage,
});

function fmt(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
function ms(n: number) {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`;
}
function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

function StatusPill({ status }: { status: string }) {
  const cfg: Record<string, { color: string; bg: string; label: string }> = {
    live: { color: '#34d399', bg: '#34d39918', label: '● Live' },
    connecting: { color: '#fbbf24', bg: '#fbbf2418', label: '◌ Connecting…' },
    error: { color: '#f87171', bg: '#f8717118', label: '✕ Error' },
    disconnected: { color: '#94a3b8', bg: '#94a3b818', label: '– Disconnected' },
  };
  const c = cfg[status] ?? cfg.disconnected;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 999, background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, border: `1px solid ${c.color}30` }}>
      {c.label}
    </span>
  );
}

function StatCard({ label, value, sub, icon, color = '#6366f1' }: { label: string; value: string; sub?: string; icon: React.ReactNode; color?: string }) {
  return (
    <div style={{ background: '#151b2e', border: '1px solid #1e2644', borderRadius: 14, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}18`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#e2e8f0', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ marginTop: 6, fontSize: 11, color: '#475569' }}>{sub}</div>}
    </div>
  );
}

function LatencyBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = value < 500 ? '#34d399' : value < 2000 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 12, fontFamily: 'monospace', color, fontWeight: 700 }}>{ms(value)}</span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: '#1e2644', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function ModelRow({ m, maxTokens }: { m: ModelTokenStats; maxTokens: number }) {
  const pct = maxTokens > 0 ? (m.totalTokens / maxTokens) * 100 : 0;
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #1e2644' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>{m.modelName}</span>
          <span style={{ fontSize: 10, color: '#475569', marginLeft: 8 }}>{m.provider}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{fmt(m.totalTokens)} tok</div>
          <div style={{ fontSize: 10, color: '#475569' }}>p50 {ms(m.latency.p50)}</div>
        </div>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: '#1e2644', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function TenantRow({ t }: { t: TenantActivity }) {
  return (
    <tr style={{ borderBottom: '1px solid #1e2644' }}>
      <td style={{ padding: '8px 12px', fontSize: 12, color: '#e2e8f0', fontWeight: 600 }}>{t.tenantName}</td>
      <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: '#60a5fa' }}>{t.openConversations}</td>
      <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: '#94a3b8' }}>{t.messagesLastHour}</td>
      <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: '#a78bfa' }}>{fmt(t.tokensLastHour)}</td>
      <td style={{ padding: '8px 12px' }}>
        {t.pendingHandoffs > 0 && <span style={{ padding: '2px 7px', borderRadius: 5, background: '#f5952518', color: '#f59525', fontSize: 10, fontWeight: 700, marginRight: 4 }}>🤚 {t.pendingHandoffs}</span>}
        {t.pendingApprovals > 0 && <span style={{ padding: '2px 7px', borderRadius: 5, background: '#f8717118', color: '#f87171', fontSize: 10, fontWeight: 700 }}>⚠ {t.pendingApprovals}</span>}
        {t.pendingHandoffs === 0 && t.pendingApprovals === 0 && <span style={{ color: '#475569', fontSize: 11 }}>—</span>}
      </td>
    </tr>
  );
}

function ErrorRow({ e }: { e: RecentError }) {
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid #1e2644', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <AlertTriangle size={14} style={{ color: '#f87171', flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.error}</div>
        <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{e.tenantName ?? e.tenantId} · {relativeTime(e.createdAt)}</div>
      </div>
    </div>
  );
}

export default function PartnerObservabilityPage() {
  const { snapshot: s, status, lastUpdated } = useObservabilitySocket();
  const maxTokens = Math.max(...(s?.tokensByModel.map(m => m.totalTokens) ?? [1]), 1);
  const maxLatency = Math.max(s?.latencyOverall.p95 ?? 0, 1000);

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity size={20} style={{ color: '#6366f1' }} /> Observability
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#475569' }}>
            Your tenants · live · updates every 10s
            {lastUpdated && ` · last update ${relativeTime(lastUpdated.toISOString())}`}
          </p>
        </div>
        <StatusPill status={status} />
      </div>

      {!s && status === 'connecting' && (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#475569' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
          <div>Connecting…</div>
        </div>
      )}

      {!s && status === 'error' && (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#f87171' }}>
          <WifiOff size={32} style={{ marginBottom: 12 }} />
          <div>Unable to connect. Check your partner permissions.</div>
        </div>
      )}

      {s && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14, marginBottom: 20 }}>
            <StatCard label="Open Sessions" value={fmt(s.openConversations)} icon={<MessageSquare size={16} />} color="#60a5fa" sub="across your tenants" />
            <StatCard label="Active Agents" value={fmt(s.activeAgents)} icon={<Bot size={16} />} color="#34d399" />
            <StatCard label="Pending Handoffs" value={fmt(s.pendingHandoffs)} icon={<Users size={16} />} color="#f59525" />
            <StatCard label="Pending Approvals" value={fmt(s.pendingApprovals)} icon={<AlertTriangle size={16} />} color="#f87171" />
            <StatCard label="Messages / 1h" value={fmt(s.messagesLastHour)} icon={<TrendingUp size={16} />} color="#a78bfa" />
            <StatCard label="Tokens / 24h" value={fmt(s.totalTokensLast24h)} icon={<Zap size={16} />} color="#fbbf24" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '270px 1fr 300px', gap: 14, marginBottom: 20 }}>
            <div style={{ background: '#151b2e', border: '1px solid #1e2644', borderRadius: 14, padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={12} /> Latency (1h)
              </div>
              <LatencyBar label="P5" value={s.latencyOverall.p5} max={maxLatency} />
              <LatencyBar label="P50" value={s.latencyOverall.p50} max={maxLatency} />
              <LatencyBar label="P95" value={s.latencyOverall.p95} max={maxLatency} />
              <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e2644', borderRadius: 8, fontSize: 11, color: '#64748b' }}>
                avg {ms(s.latencyOverall.avg)} · n={s.latencyOverall.count}
              </div>
            </div>

            <div style={{ background: '#151b2e', border: '1px solid #1e2644', borderRadius: 14, padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Zap size={12} /> Token Spend by Model (24h)
              </div>
              {s.tokensByModel.length === 0
                ? <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', padding: '2rem 0' }}>No model activity yet</div>
                : s.tokensByModel.map(m => <ModelRow key={`${m.provider}/${m.modelName}`} m={m} maxTokens={maxTokens} />)}
            </div>

            <div style={{ background: '#151b2e', border: '1px solid #1e2644', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e2644', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={12} style={{ color: '#f87171' }} /> Recent Errors
              </div>
              {s.recentErrors.length === 0
                ? <div style={{ color: '#34d399', fontSize: 12, textAlign: 'center', padding: '2rem 0' }}>✓ No recent errors</div>
                : <div style={{ maxHeight: 300, overflowY: 'auto' }}>{s.recentErrors.map(e => <ErrorRow key={e.id} e={e} />)}</div>}
            </div>
          </div>

          <div style={{ background: '#151b2e', border: '1px solid #1e2644', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e2644', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Tenant Activity
            </div>
            {s.tenants.length === 0
              ? <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', padding: '2rem 0' }}>No active tenants</div>
              : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1e2644' }}>
                      {['Tenant', 'Open Sessions', 'Msgs/1h', 'Tokens/1h', 'Alerts'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{s.tenants.map(t => <TenantRow key={t.tenantId} t={t} />)}</tbody>
                </table>}
          </div>
        </>
      )}
    </div>
  );
}
